import { Disclosure } from '@headlessui/react';
import { Checkbox } from 'components/Checkbox';
import { Icon } from 'components/Icon';
import { LoadingBounce } from 'components/Loading';
import { groupBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { countriesMappedByRegion } from 'utils/countries';
import {
  Mandates,
  MandateSelectionFilter,
  Portfolio,
  Principal,
  SegmentName,
  SelectedMandates,
} from '../../utils/types';

type PortfolioLevel1Group = {
  level1Id: string;
  level1Name: string;
  portfolios: Portfolio[];
};

const PORTFOLIO_LEVEL_SEPARATOR = ' / ';

const level1IdOf = (portfolio: Portfolio) =>
  portfolio.id.split(PORTFOLIO_LEVEL_SEPARATOR)[0];

const level1NameOf = (portfolio: Portfolio) =>
  portfolio.name.split(PORTFOLIO_LEVEL_SEPARATOR)[0];

const level2NameOf = (portfolio: Portfolio) =>
  portfolio.name.split(PORTFOLIO_LEVEL_SEPARATOR)[1] ?? portfolio.name;

const groupPortfoliosByLevel1 = (
  portfolios: Portfolio[],
): PortfolioLevel1Group[] => {
  const grouped = groupBy(portfolios, level1IdOf);

  return Object.entries(grouped).map(([level1Id, level1Portfolios]) => ({
    level1Id,
    level1Name: level1NameOf(level1Portfolios[0]),
    portfolios: level1Portfolios,
  }));
};

export type MandateSelectionProps = {
  mandates?: Mandates;
  handleMandateFilterChange?: (selectedMandates: SelectedMandates) => void;
  initMandateFilters?: MandateSelectionFilter;
  selectedPrincipal: Principal;
  isGlobalUser?: boolean;
  disabled?: boolean;
};

export const MandateSelection: React.FC<MandateSelectionProps> = ({
  mandates,
  handleMandateFilterChange,
  initMandateFilters,
  selectedPrincipal,
  isGlobalUser = false,
  disabled,
}) => {
  const { t } = useTranslation();

  // ALL DATA LOGIC
  const availableMarketSegments = mandates?.marketSegments ?? [];

  const availablePortfolios = mandates?.portfolios ?? [];

  const availableCountries =
    mandates?.countries.map((country) => ({
      ISO_A3: country.iso,
      name: country.name,
    })) ?? [];

  const availableUsaTerritories =
    mandates?.territories?.map(({ name }) => name) ?? [];

  const generateSelectedMandates = (
    filters: MandateSelectionFilter | undefined,
    selectedPrincipalId: string,
  ) => ({
    portfolio: filters?.[selectedPrincipalId]?.portfolio || [],
    country: filters?.[selectedPrincipalId]?.country || [],
    marketSegment: filters?.[selectedPrincipalId]?.marketSegment || [],
    territory: filters?.[selectedPrincipalId]?.territory || [],
  });

  const initSelectedMandates = useMemo(
    () => generateSelectedMandates(initMandateFilters, selectedPrincipal.id),
    [initMandateFilters, selectedPrincipal],
  );

  // CHECKBOX LOGIC
  const [sortCheckedBoxes, setSortCheckedBoxes] =
    useState<SelectedMandates>(initSelectedMandates);

  const hasAllMandates =
    isGlobalUser || initMandateFilters?.[selectedPrincipal.id]?.hasAllMandates;

  const handleSingleCheckboxChange = (value: string, segment: SegmentName) => {
    const checkedValues = sortCheckedBoxes[segment];
    const newCheckboxValues = {
      ...sortCheckedBoxes, // Deep copy not working
      [segment]: [...checkedValues].includes(value)
        ? [...checkedValues].filter((str) => str !== value)
        : [...checkedValues, value],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const handleSingleTerritoryCheckboxChange = (value: string) => {
    const checkedValues = sortCheckedBoxes.territory;

    let newCheckboxValues;

    newCheckboxValues = {
      ...sortCheckedBoxes, // Deep copy not working
      territory: [...checkedValues].includes(value)
        ? [...checkedValues].filter((str) => str !== value)
        : [...checkedValues, value],
    };

    const hasAllTerritoriesSelected = (updatedValues: {
      territory: string[];
      portfolio: string[];
      country: string[];
      marketSegment: string[];
    }) =>
      availableUsaTerritories.every((territory) =>
        updatedValues.territory.includes(territory),
      );

    if (hasAllTerritoriesSelected(newCheckboxValues)) {
      newCheckboxValues = {
        ...newCheckboxValues,
        country: [...newCheckboxValues.country, 'USA'],
      };
    } else {
      newCheckboxValues = {
        ...newCheckboxValues,
        country: [...newCheckboxValues.country].filter(
          (countryName) => countryName !== 'USA',
        ),
      };
    }

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const handleAllCheckboxChange = (
    val: string[],
    isAllChecked: boolean,
    segment: string,
  ) => {
    const newCheckboxValues = {
      ...sortCheckedBoxes,
      [segment]: isAllChecked ? [...val] : [],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  useEffect(() => {
    setSortCheckedBoxes(initSelectedMandates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAllMandates, initMandateFilters]);

  const handleAllPortfolioChange = (
    portfolios: Portfolio[],
    isAllChecked: boolean,
    segment: string,
  ) => {
    const portfolioIds = portfolios.map(({ id }) => id);
    const newCheckboxValues = {
      ...sortCheckedBoxes,
      [segment]: isAllChecked ? [...portfolioIds] : [],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const regions = countriesMappedByRegion(availableCountries);

  const portfolioLevel1Groups =
    selectedPrincipal.portfolioLevel === 2
      ? groupPortfoliosByLevel1(availablePortfolios)
      : null;

  const handleAllRegionsCheckboxChange = (
    region: string,
    isAllChecked: boolean,
  ) => {
    const selectedCountries = sortCheckedBoxes.country;

    const countriesBySelectedRegion = regions[region].map(
      ({ ISO_A3 }) => ISO_A3,
    );

    const updatedCountries = selectedCountries.filter(
      (country) => !countriesBySelectedRegion.includes(country),
    );

    const selectedRegionIsAmericas = region === 'AMERICAS';

    const newCheckboxValues = {
      ...sortCheckedBoxes,
      country: isAllChecked
        ? [...countriesBySelectedRegion, ...selectedCountries]
        : [...updatedCountries],
      ...(selectedRegionIsAmericas && {
        territory: isAllChecked ? [...availableUsaTerritories] : [],
      }),
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  // A Level 1 category assigned via the one-click checkbox is stored as a single wildcard
  // entry (the bare Level 1 id, no ' / ' separator) rather than an enumerated list of its
  // Level 2 mandates. `portfolioFit` (functions/src/helpers/calcMandatesPerUser.ts) already
  // resolves a bare id against every current Level 2 mandate under that category, so this
  // stays dynamically in sync with mandates added later — see ADR-0001. Deselecting an
  // individual Level 2 mandate converts the category to a static, explicit list (below).
  const isLevel1Wildcarded = (level1Id: string) =>
    sortCheckedBoxes.portfolio.includes(level1Id);

  const handleAllLevel1PortfoliosChange = (
    level1Group: PortfolioLevel1Group,
    isAllChecked: boolean,
  ) => {
    const { level1Id, portfolios: level1Portfolios } = level1Group;
    const level1PortfolioIds = level1Portfolios.map(({ id }) => id);
    const otherSelectedPortfolios = sortCheckedBoxes.portfolio.filter(
      (portfolioId) =>
        !level1PortfolioIds.includes(portfolioId) && portfolioId !== level1Id,
    );

    const newCheckboxValues = {
      ...sortCheckedBoxes,
      portfolio: isAllChecked
        ? [...otherSelectedPortfolios, level1Id]
        : otherSelectedPortfolios,
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const handleSingleLevel2PortfolioChange = (
    portfolio: Portfolio,
    level1Group: PortfolioLevel1Group,
  ) => {
    const { level1Id, portfolios: level1Portfolios } = level1Group;

    if (!isLevel1Wildcarded(level1Id)) {
      handleSingleCheckboxChange(portfolio.id, 'portfolio');
      return;
    }

    const explicitSiblingIds = level1Portfolios
      .filter(({ id }) => id !== portfolio.id)
      .map(({ id }) => id);

    const newCheckboxValues = {
      ...sortCheckedBoxes,
      portfolio: [
        ...sortCheckedBoxes.portfolio.filter((id) => id !== level1Id),
        ...explicitSiblingIds,
      ],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const hasAllLevel1PortfoliosSelected = (
    level1Group: PortfolioLevel1Group,
  ) => {
    const { level1Id, portfolios: level1Portfolios } = level1Group;

    if (isLevel1Wildcarded(level1Id)) return true;

    const selectedPortfolios = sortCheckedBoxes.portfolio;

    return level1Portfolios.every(({ id }) => selectedPortfolios.includes(id));
  };

  const hasSomeLevel1PortfoliosSelected = (
    level1Group: PortfolioLevel1Group,
  ) => {
    const { level1Id, portfolios: level1Portfolios } = level1Group;

    if (isLevel1Wildcarded(level1Id)) return false;

    const selectedPortfolios = sortCheckedBoxes.portfolio;
    const hasAllSelected = hasAllLevel1PortfoliosSelected(level1Group);

    return (
      !hasAllSelected &&
      level1Portfolios.some(({ id }) => selectedPortfolios.includes(id))
    );
  };

  const handleAllLevel1GroupsChange = (
    level1Groups: PortfolioLevel1Group[],
    isAllChecked: boolean,
  ) => {
    const newCheckboxValues = {
      ...sortCheckedBoxes,
      portfolio: isAllChecked
        ? level1Groups.map(({ level1Id }) => level1Id)
        : [],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  const hasAllLevel1GroupsSelected = (level1Groups: PortfolioLevel1Group[]) =>
    level1Groups.every((level1Group) =>
      hasAllLevel1PortfoliosSelected(level1Group),
    );

  const hasSomeLevel1GroupsSelected = (
    level1Groups: PortfolioLevel1Group[],
  ) => {
    const hasAllSelected = hasAllLevel1GroupsSelected(level1Groups);

    return (
      !hasAllSelected &&
      level1Groups.some(
        (level1Group) =>
          hasAllLevel1PortfoliosSelected(level1Group) ||
          hasSomeLevel1PortfoliosSelected(level1Group),
      )
    );
  };

  const hasAllCountriesSelected = (region: string) => {
    const selectedCountries = sortCheckedBoxes.country;

    const countriesBySelectedRegion = regions[region].map(
      ({ ISO_A3 }) => ISO_A3,
    );

    return countriesBySelectedRegion.every((country) =>
      selectedCountries.includes(country),
    );
  };

  const hasSomeCountriesSelected = (region: string) => {
    const selectedCountries = sortCheckedBoxes.country;

    const countriesBySelectedRegion = regions[region].map(
      ({ ISO_A3 }) => ISO_A3,
    );

    const hasAllSelected = hasAllCountriesSelected(region);

    return (
      !hasAllSelected &&
      countriesBySelectedRegion.some((country) =>
        selectedCountries.includes(country),
      )
    );
  };

  const displayUSAsIntermediate = () => {
    const selectedTerritories = sortCheckedBoxes.territory;

    return (
      !!selectedTerritories.length &&
      !availableUsaTerritories.every((territory) =>
        selectedTerritories.includes(territory),
      )
    );
  };

  const displayRegionAsIntermediate = (region: string): boolean => {
    let hasSomeSelected;

    if (region === 'AMERICAS') {
      const usaAsIntermediate = displayUSAsIntermediate();

      hasSomeSelected = usaAsIntermediate || hasSomeCountriesSelected(region);
    } else {
      hasSomeSelected = hasSomeCountriesSelected(region);
    }

    return hasSomeSelected;
  };

  const handleUSACheckboxChange = () => {
    handleSingleCheckboxChange('USA', 'country');

    const checkedValues = sortCheckedBoxes.country;
    const newCheckboxValues = {
      ...sortCheckedBoxes,
      country: checkedValues.includes('USA')
        ? [...checkedValues].filter((str) => str !== 'USA')
        : [...checkedValues, 'USA'],
      territory: checkedValues.includes('USA')
        ? []
        : [...availableUsaTerritories],
    };

    setSortCheckedBoxes(newCheckboxValues);
    handleMandateFilterChange?.(newCheckboxValues);
  };

  return (
    <div className="grid grid-flow-row grid-cols-3 gap-4">
      <div
        data-testid="portfolio-checkboxes"
        className="bg-grey-100 max-w-408px col-span-1 rounded p-2"
      >
        <h2 className="text-grey-700 mb-3 font-medium">
          {t('modals:addUserModal:portfolio')}
        </h2>
        {availablePortfolios.length > 0 ? (
          portfolioLevel1Groups ? (
            <>
              <Checkbox
                displayAsIntermediate={hasSomeLevel1GroupsSelected(
                  portfolioLevel1Groups,
                )}
                label={t('modals:addUserModal:all')}
                onChange={(isAllChecked) =>
                  handleAllLevel1GroupsChange(
                    portfolioLevel1Groups,
                    isAllChecked,
                  )
                }
                initialChecked={
                  hasAllMandates ||
                  hasAllLevel1GroupsSelected(portfolioLevel1Groups)
                }
                disabledVisibleLabel={hasAllMandates || disabled}
              />
              {portfolioLevel1Groups.map((level1Group) => (
                <Disclosure key={level1Group.level1Id}>
                  {({ open }) => (
                    <div
                      className="mb-1"
                      data-testid={`portfolio-level1-group-${level1Group.level1Id}`}
                    >
                      <div className="flex items-center">
                        <Disclosure.Button
                          className="mr-1 block h-4 w-4"
                          data-testid="portfolio-level1-toggle"
                        >
                          <Icon name={open ? 'ArrowDown' : 'ArrowRight'} />
                        </Disclosure.Button>
                        <Checkbox
                          initialChecked={
                            hasAllMandates
                              ? true
                              : hasAllLevel1PortfoliosSelected(level1Group)
                          }
                          label={level1Group.level1Name}
                          onChange={(isAllChecked) =>
                            handleAllLevel1PortfoliosChange(
                              level1Group,
                              isAllChecked,
                            )
                          }
                          disabledVisibleLabel={hasAllMandates || disabled}
                          displayAsIntermediate={hasSomeLevel1PortfoliosSelected(
                            level1Group,
                          )}
                        />
                      </div>
                      <Disclosure.Panel className="mt-1 space-y-1">
                        {level1Group.portfolios.map((portfolio) => (
                          <div className="ml-8" key={portfolio.id}>
                            <Checkbox
                              initialChecked={
                                hasAllMandates
                                  ? true
                                  : isLevel1Wildcarded(level1Group.level1Id) ||
                                    !!sortCheckedBoxes.portfolio?.find(
                                      (option) => option === portfolio.id,
                                    )
                              }
                              label={level2NameOf(portfolio)}
                              onChange={() =>
                                handleSingleLevel2PortfolioChange(
                                  portfolio,
                                  level1Group,
                                )
                              }
                              disabledVisibleLabel={hasAllMandates || disabled}
                            />
                          </div>
                        ))}
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              ))}
            </>
          ) : (
            <>
              <Checkbox
                displayAsIntermediate={
                  availablePortfolios.length !==
                    sortCheckedBoxes.portfolio.length &&
                  sortCheckedBoxes.portfolio.length !== 0
                }
                label={t('modals:addUserModal:all')}
                onChange={(isAllChecked) => {
                  handleAllPortfolioChange(
                    availablePortfolios,
                    isAllChecked,
                    'portfolio',
                  );
                }}
                initialChecked={
                  hasAllMandates ||
                  sortCheckedBoxes.portfolio.length ===
                    availablePortfolios.length
                }
                disabledVisibleLabel={hasAllMandates || disabled}
              />

              {availablePortfolios.map((portfolio) => (
                <div className="my-1" key={`${portfolio.name}${portfolio.id}`}>
                  <Checkbox
                    initialChecked={
                      hasAllMandates
                        ? true
                        : !!sortCheckedBoxes.portfolio?.find(
                            (option) => option === portfolio.id,
                          )
                    }
                    label={portfolio.name}
                    onChange={() =>
                      handleSingleCheckboxChange(
                        portfolio.id as string,
                        'portfolio',
                      )
                    }
                    disabledVisibleLabel={hasAllMandates || disabled}
                  />
                </div>
              ))}
            </>
          )
        ) : (
          <LoadingBounce />
        )}
      </div>
      <div
        data-testid="countries-checkboxes"
        className="bg-grey-100 max-w-408px col-span-1 rounded p-2"
      >
        <h2 className="text-grey-700 mb-3 font-medium">
          {t('modals:addUserModal:country')}
        </h2>

        {Object.keys(regions).length > 0 ? (
          Object.entries(regions).map(([region, countries]) => {
            if (!countries.length) return null;

            return (
              <Disclosure key={region}>
                {({ open }) => (
                  <div className="mb-1">
                    <div className="flex items-center">
                      <Disclosure.Button className="mr-1 block h-4 w-4">
                        <Icon name={open ? 'ArrowDown' : 'ArrowRight'} />
                      </Disclosure.Button>
                      <Checkbox
                        initialChecked={
                          hasAllMandates
                            ? true
                            : hasAllCountriesSelected(region)
                        }
                        label={region}
                        onChange={(isAllChecked) =>
                          handleAllRegionsCheckboxChange(region, isAllChecked)
                        }
                        disabledVisibleLabel={hasAllMandates || disabled}
                        displayAsIntermediate={displayRegionAsIntermediate(
                          region,
                        )}
                      />
                    </div>
                    <Disclosure.Panel className="mt-1 space-y-1">
                      {countries.map(({ ISO_A3, name }) => {
                        if (ISO_A3 === 'USA' && availableUsaTerritories.length)
                          return (
                            <Disclosure key={ISO_A3}>
                              {({ open }) => (
                                <div className="mb-1 ml-8">
                                  <div className="flex items-center">
                                    <Disclosure.Button className="mr-1 block h-4 w-4">
                                      <Icon
                                        name={open ? 'ArrowDown' : 'ArrowRight'}
                                      />
                                    </Disclosure.Button>
                                    <Checkbox
                                      initialChecked={
                                        hasAllMandates
                                          ? true
                                          : !!sortCheckedBoxes.country?.find(
                                              (option) => option === ISO_A3,
                                            )
                                      }
                                      label={name}
                                      onChange={handleUSACheckboxChange}
                                      displayAsIntermediate={displayUSAsIntermediate()}
                                      disabledVisibleLabel={
                                        hasAllMandates || disabled
                                      }
                                    />
                                  </div>
                                  <Disclosure.Panel className="mt-1 space-y-1">
                                    {availableUsaTerritories?.map(
                                      (territory) => (
                                        <div className="ml-5" key={territory}>
                                          <Checkbox
                                            initialChecked={
                                              hasAllMandates
                                                ? true
                                                : !!sortCheckedBoxes.territory?.find(
                                                    (option) =>
                                                      option === territory,
                                                  )
                                            }
                                            label={territory}
                                            onChange={() =>
                                              handleSingleTerritoryCheckboxChange(
                                                territory,
                                              )
                                            }
                                            disabledVisibleLabel={
                                              hasAllMandates || disabled
                                            }
                                          />
                                        </div>
                                      ),
                                    )}
                                  </Disclosure.Panel>
                                </div>
                              )}
                            </Disclosure>
                          );

                        return (
                          <div className="ml-8" key={ISO_A3}>
                            <Checkbox
                              initialChecked={
                                hasAllMandates
                                  ? true
                                  : !!sortCheckedBoxes.country?.find(
                                      (option) => option === ISO_A3,
                                    )
                              }
                              label={name}
                              onChange={() =>
                                handleSingleCheckboxChange(ISO_A3, 'country')
                              }
                              disabledVisibleLabel={hasAllMandates || disabled}
                            />
                          </div>
                        );
                      })}
                    </Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            );
          })
        ) : (
          <LoadingBounce />
        )}
      </div>
      <div
        data-testid="marketSegment-checkboxes"
        className="bg-grey-100 max-w-408px col-span-1 rounded p-2"
      >
        <h2 className="text-grey-700 mb-3 font-medium">
          {t('modals:addUserModal:marketSegment')}
        </h2>

        {availableMarketSegments.length > 0 ? (
          <>
            <Checkbox
              displayAsIntermediate={
                availableMarketSegments.length !==
                  sortCheckedBoxes.marketSegment.length &&
                sortCheckedBoxes.marketSegment.length !== 0
              }
              label={t('modals:addUserModal:all')}
              onChange={(isAllChecked) => {
                handleAllCheckboxChange(
                  availableMarketSegments.map((val) => {
                    return typeof val === 'string' ? val : val.code;
                  }),
                  isAllChecked,
                  'marketSegment',
                );
              }}
              initialChecked={
                hasAllMandates ||
                sortCheckedBoxes.marketSegment.length ===
                  availableMarketSegments.length
              }
              disabledVisibleLabel={hasAllMandates || disabled}
            />

            {availableMarketSegments.map((val) => (
              <div className="my-1" key={val.code}>
                <Checkbox
                  initialChecked={
                    hasAllMandates
                      ? true
                      : !!sortCheckedBoxes.marketSegment?.find(
                          (option) => option === val.code,
                        )
                  }
                  label={val.name}
                  onChange={() =>
                    handleSingleCheckboxChange(val.code, 'marketSegment')
                  }
                  disabledVisibleLabel={hasAllMandates || disabled}
                />
              </div>
            ))}
          </>
        ) : (
          <LoadingBounce />
        )}
      </div>
    </div>
  );
};
